import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { api } from "../lib/api";
import { Card } from "../components/ui/card";
import { Badge } from "../components/ui/badge";
import {
  Calendar, LogIn, LogOut, Ship, ChevronRight, Sparkles,
  AlertTriangle, Users, ChevronDown, ChevronUp,
} from "lucide-react";
import { boatImage } from "../lib/boatImages";
import { useAuth } from "../lib/auth";
import WelcomeBanner from "../components/WelcomeBanner";
import SeasonModeCard from "../components/SeasonModeCard";

const dayStr = (d) => d.toISOString().slice(0, 10);
const fmt = (iso) => {
  if (!iso) return "";
  const [y, m, d] = iso.slice(0, 10).split("-");
  return `${d}.${m}.${y}`;
};

function relLabel(daysFromToday) {
  if (daysFromToday === 0) return "I dag";
  if (daysFromToday === 1) return "I morgen";
  if (daysFromToday === -1) return "I går";
  if (daysFromToday < 0) return `${Math.abs(daysFromToday)} dager siden`;
  return `om ${daysFromToday} dager`;
}

/** Operations dashboard for vask + service: clear bird's-eye view of upcoming
 *  bookings, plus the boats that need their attention right now. */
export default function OperationsDashboard() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const isVask = user?.role === "vask";
  const isService = user?.role === "service";
  const [bookings, setBookings] = useState([]);
  const [boats, setBoats] = useState([]);
  const [vaskeplan, setVaskeplan] = useState({ boats: [] });
  const [defects, setDefects] = useState([]);
  const [showLater, setShowLater] = useState(false);

  useEffect(() => {
    (async () => {
      const [bk, bs, vp, df] = await Promise.all([
        api.get("/bookings").catch(() => ({ data: [] })),
        api.get("/boats").catch(() => ({ data: [] })),
        api.get("/vaske-plan").catch(() => ({ data: { boats: [] } })),
        api.get("/defects").catch(() => ({ data: [] })),
      ]);
      setBookings(bk.data || []);
      setBoats(bs.data || []);
      setVaskeplan(vp.data || { boats: [] });
      setDefects((df.data || []).filter((d) => d.status !== "resolved" && d.status !== "known_issue"));
    })();
  }, []);

  const boatBase = "/admin"; // both worker roles use shared /admin/boats/:id route

  // Jump directly to the most severe open defect on a boat – mirrors the
  // fleet dashboard logic so warning/grounded chips & avvik counts are
  // first-class clickable everywhere.
  const goToBoatDefect = (boatId, severityPreference) => (e) => {
    e.preventDefault();
    e.stopPropagation();
    const all = defects.filter((d) => d.boat_id === boatId);
    let pool = all;
    if (severityPreference) {
      const red = all.filter((d) => d.severity === "red");
      const yellow = all.filter((d) => d.severity === "yellow");
      pool = red.length > 0 ? red : (yellow.length > 0 ? yellow : all);
    }
    const base = user?.role === "service" ? "/service"
      : user?.role === "vask" ? "/vask" : "/admin";
    if (pool.length === 1) {
      navigate(`${base}/defects?focus=${pool[0].id}`);
    } else {
      navigate(`${base}/defects?boat=${boatId}`);
    }
  };

  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const todayIso = dayStr(today);
  const in7 = new Date(today); in7.setDate(in7.getDate() + 7);
  const in7Iso = dayStr(in7);

  // Build "events" (one per check-in + one per check-out) so the timeline
  // shows the actual turnaround moments rather than entire stay-ranges.
  const events = [];
  for (const b of bookings) {
    const start = (b.start_date || "").slice(0, 10);
    const end = (b.end_date || "").slice(0, 10);
    if (!start || !end) continue;
    events.push({ ...b, _kind: "checkin", _date: start, _time: "14:00" });
    events.push({ ...b, _kind: "checkout", _date: end, _time: "10:00" });
  }
  events.sort((a, b) =>
    (a._date + a._time).localeCompare(b._date + b._time) ||
    (a._kind === "checkin" ? -1 : 1) // outs come after ins on the same minute
  );

  const future = events.filter((e) => e._date >= todayIso);
  const todayEvents = future.filter((e) => e._date === todayIso);
  const next7 = future.filter((e) => e._date > todayIso && e._date <= in7Iso);
  const later = future.filter((e) => e._date > in7Iso);

  const boatById = Object.fromEntries(boats.map((b) => [b.id, b]));
  const vpById = Object.fromEntries((vaskeplan.boats || []).map((v) => [v.boat?.id, v]));
  const openDefectsByBoat = defects.reduce((acc, d) => {
    acc[d.boat_id] = (acc[d.boat_id] || 0) + 1;
    return acc;
  }, {});

  const taskBoats = boats.map((b) => {
    const vp = vpById[b.id] || {};
    return {
      ...b,
      _cleaning_pending: vp.pending_count ?? b.cleaning_pending ?? 0,
      _open_defects: openDefectsByBoat[b.id] || b.open_defects || 0,
    };
  });

  // Tasks to surface – per role:
  //   vask: boats with pending cleaning items > 0, sorted by next booking
  //   service: boats with open defects OR pending check-in/out checklists
  let myTasks;
  if (isVask) {
    myTasks = taskBoats
      .filter((b) => b._cleaning_pending > 0)
      .sort((a, b) => (a.next_booking?.start_date || "9999").localeCompare(b.next_booking?.start_date || "9999"));
  } else {
    // service
    myTasks = taskBoats
      .filter((b) => b._open_defects > 0 || (b.readiness && b.readiness !== "ready"))
      .sort((a, b) => (b._open_defects || 0) - (a._open_defects || 0));
  }

  const eventRow = (e) => {
    const boat = boatById[e.boat_id];
    const d = new Date(e._date);
    d.setHours(0, 0, 0, 0);
    const days = Math.round((d - today) / 86400000);
    const isCheckin = e._kind === "checkin";
    return (
      <Link
        key={`${e.id}-${e._kind}`}
        to={`${boatBase}/boats/${e.boat_id}`}
        data-testid={`event-${e._kind}-${e.id}`}
        className="flex items-center gap-3 p-3 rounded-md bg-white border border-[#E2E8F0] hover:border-[#1DA3B3] transition-colors"
      >
        <div
          className={`flex-shrink-0 w-12 h-12 rounded-md flex flex-col items-center justify-center ${
            isCheckin
              ? "bg-[#1DA3B3]/10 text-[#1DA3B3]"
              : "bg-[#BD984D]/15 text-[#A57F39]"
          }`}
        >
          {isCheckin ? <LogIn className="w-4 h-4" /> : <LogOut className="w-4 h-4" />}
          <span className="text-[10px] font-bold mt-0.5">{e._time}</span>
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <span className="text-[10px] uppercase tracking-[0.18em] text-[#A57F39] font-semibold">
              {boat?.name || "—"}
            </span>
            <span
              className={`text-[10px] font-semibold px-1.5 py-0.5 rounded ${
                isCheckin ? "bg-[#1DA3B3] text-white" : "bg-[#BD984D] text-white"
              }`}
            >
              {isCheckin ? "INN" : "UT"}
            </span>
          </div>
          <div className="text-sm font-semibold text-[#0C1824] truncate">
            {e.customer_name || "Ukjent kunde"}
          </div>
          <div className="text-xs text-[#2B3F4D] flex items-center gap-2 flex-wrap mt-0.5">
            <span><Calendar className="w-3 h-3 inline -mt-0.5 mr-0.5" />{fmt(e._date)}</span>
            <span className="text-[#A57F39]">{relLabel(days)}</span>
            {e.num_guests && (
              <span><Users className="w-3 h-3 inline -mt-0.5 mr-0.5" />{e.num_guests} pers.</span>
            )}
          </div>
        </div>
        <ChevronRight className="w-4 h-4 text-[#A57F39] flex-shrink-0" />
      </Link>
    );
  };

  const taskRow = (b) => {
    const activeEnd = b.active_booking?.end_date?.slice(0, 10);
    let activeOut = null;
    if (activeEnd) {
      const d = new Date(activeEnd); d.setHours(0, 0, 0, 0);
      const days = Math.round((d - today) / 86400000);
      activeOut = days <= 0 ? "Ut i dag kl. 10:00" : `Ut om ${days} dager`;
    }
    return (
    <Link
      key={b.id}
      to={`${boatBase}/boats/${b.id}`}
      data-testid={`task-row-${b.id}`}
      className="flex items-center gap-3 p-3 rounded-md bg-white border border-[#E2E8F0] hover:border-[#1DA3B3] transition-colors"
    >
      <img
        src={boatImage(b.id)}
        alt=""
        className="w-12 h-12 object-cover rounded-md flex-shrink-0"
      />
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2 flex-wrap">
          <span className="text-sm font-semibold text-[#0C1824]">{b.name}</span>
          {b.status === "ute" && (
            <Badge className="bg-[#0C1824] text-white text-[10px]">UTE</Badge>
          )}
          {activeOut && (
            <Badge className="bg-[#BD984D]/20 text-[#A57F39] text-[10px]" data-testid={`task-checkout-${b.id}`}>
              <LogOut className="w-3 h-3 mr-0.5 inline -mt-0.5" />
              {activeOut}
            </Badge>
          )}
        </div>
        <div className="text-xs text-[#2B3F4D] flex items-center gap-3 mt-0.5 flex-wrap">
          {isVask && b._cleaning_pending > 0 && (
            <span className="text-red-600 font-medium">
              <Sparkles className="w-3 h-3 inline -mt-0.5 mr-0.5" />
              {b._cleaning_pending} renholdspunkter
            </span>
          )}
          {isService && b._open_defects > 0 && (
            <button
              type="button"
              onClick={goToBoatDefect(b.id, false)}
              data-testid={`task-defect-count-${b.id}`}
              className="text-red-600 font-medium hover:underline focus:underline"
              title="Åpne avvik"
            >
              <AlertTriangle className="w-3 h-3 inline -mt-0.5 mr-0.5" />
              {b._open_defects} åpne avvik
            </button>
          )}
          {isService && b.readiness && b.readiness !== "ready" && (
            <Badge
              onClick={goToBoatDefect(b.id, true)}
              role="button"
              tabIndex={0}
              onKeyDown={(e) => {
                if (e.key === "Enter" || e.key === " ") goToBoatDefect(b.id, true)(e);
              }}
              data-testid={`task-readiness-${b.id}`}
              title="Åpne saken som forårsaker dette"
              className={`cursor-pointer hover:brightness-110 transition ${
                b.readiness === "grounded"
                  ? "bg-red-100 text-red-700"
                  : "bg-[#BD984D]/20 text-[#A57F39]"
              }`}
            >
              {b.readiness === "grounded" ? "Grunnet" : "Advarsel"}
            </Badge>
          )}
          {!activeEnd && b.next_booking?.start_date && (
            <span><Calendar className="w-3 h-3 inline -mt-0.5 mr-0.5" />
              Neste inn: {fmt(b.next_booking.start_date.slice(0,10))}
            </span>
          )}
        </div>
      </div>
      <ChevronRight className="w-4 h-4 text-[#A57F39] flex-shrink-0" />
    </Link>
    );
  };

  return (
    <div className="space-y-6">
      <WelcomeBanner />
      <SeasonModeCard />

      <div>
        <div className="text-xs uppercase tracking-[0.32em] text-[#A57F39]">Driftsoversikt</div>
        <h1 className="text-2xl sm:text-3xl font-bold text-[#0C1824] mt-1">
          {isVask ? "Hva som skal vaskes" : "Hva som skal gjøres"}
        </h1>
        <p className="text-sm text-[#2B3F4D] mt-1">
          {isVask
            ? "Båter som trenger renhold før neste kunde – og kommende innsjekk."
            : "Båter med åpne saker – og kommende innsjekk og utsjekk."}
        </p>
      </div>

      {/* "Today" highlights */}
      {todayEvents.length > 0 && (
        <Card className="p-5 bg-gradient-to-br from-[#1DA3B3]/10 to-[#BD984D]/10 border border-[#1DA3B3]/30" data-testid="ops-today">
          <div className="flex items-center gap-2 mb-3">
            <span className="text-xs uppercase tracking-[0.18em] text-[#A57F39] font-semibold">I dag</span>
            <Badge className="bg-[#0C1824] text-white">{todayEvents.length}</Badge>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
            {todayEvents.map(eventRow)}
          </div>
        </Card>
      )}

      {/* Boats on active trips – quick read-out so vask/service know which
          boats are *currently with a customer* and when they return. */}
      {(() => {
        const outBoats = taskBoats.filter((b) => b.active_booking);
        if (outBoats.length === 0) return null;
        return (
          <div data-testid="ops-out-now">
            <div className="flex items-center gap-2 mb-3">
              <LogOut className="w-4 h-4 text-[#A57F39]" />
              <h2 className="text-lg font-semibold text-[#0C1824]">På tur nå</h2>
              <Badge className="bg-[#0C1824] text-white">{outBoats.length}</Badge>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
              {outBoats.map(taskRow)}
            </div>
          </div>
        );
      })()}

      {/* Tasks per role */}
      <div>
        <div className="flex items-center justify-between mb-3 flex-wrap gap-2">
          <div>
            <h2 className="text-lg font-semibold text-[#0C1824] flex items-center gap-2">
              {isVask
                ? <><Sparkles className="w-4 h-4 text-[#1DA3B3]" /> Renhold som gjenstår</>
                : <><AlertTriangle className="w-4 h-4 text-[#1DA3B3]" /> Båter med åpne saker</>}
            </h2>
            <p className="text-xs text-[#2B3F4D]">
              {isVask
                ? "Trykk en båt for å åpne sjekklisten og inventaret."
                : "Trykk en båt for å åpne sjekklister, avvik og status."}
            </p>
          </div>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-2" data-testid="ops-tasks">
          {myTasks.length === 0 ? (
            <Card className="p-6 text-center text-sm text-[#2B3F4D] md:col-span-2">
              {isVask ? "✓ Alle båter er klare for utleie." : "✓ Ingen åpne saker."}
            </Card>
          ) : (
            myTasks.map(taskRow)
          )}
        </div>
      </div>

      {/* Next 7 days */}
      <div>
        <div className="flex items-center gap-2 mb-3">
          <Ship className="w-4 h-4 text-[#1DA3B3]" />
          <h2 className="text-lg font-semibold text-[#0C1824]">Kommende (neste 7 dager)</h2>
          <Badge className="bg-[#1DA3B3]/15 text-[#1DA3B3]">{next7.length}</Badge>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-2" data-testid="ops-next7">
          {next7.length === 0 ? (
            <Card className="p-6 text-center text-sm text-[#2B3F4D] md:col-span-2">
              Ingen innsjekk eller utsjekk de neste 7 dagene.
            </Card>
          ) : (
            next7.map(eventRow)
          )}
        </div>
      </div>

      {/* Later (collapsible) */}
      {later.length > 0 && (
        <div>
          <button
            type="button"
            onClick={() => setShowLater((v) => !v)}
            data-testid="ops-later-toggle"
            className="flex items-center gap-2 text-sm font-semibold text-[#0C1824] hover:text-[#1DA3B3] transition-colors"
          >
            {showLater ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
            Senere ({later.length})
          </button>
          {showLater && (
            <div
              className="grid grid-cols-1 md:grid-cols-2 gap-2 mt-3"
              data-testid="ops-later-list"
            >
              {later.slice(0, 50).map(eventRow)}
              {later.length > 50 && (
                <Card className="p-3 text-center text-xs text-[#2B3F4D] md:col-span-2">
                  + {later.length - 50} flere – se{" "}
                  <Link to="/admin/bookings" className="text-[#1DA3B3] underline">
                    bookingkalenderen
                  </Link>
                </Card>
              )}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
